import type { Signal } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { combineLatest, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { GtmService } from '@fcom/common/gtm/services/gtm.service';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common/interfaces/gtm.interface';
import { JoinService } from '@fcom/common/login/services/join.service';
import { LoginService } from '@fcom/common/login/services/login.service';
import { LoginStep } from '@fcom/core-api/login/interfaces/login.interface';
import * as LoginActions from '@fcom/core/actions/login.actions';
import type { AppState } from '@fcom/core/interfaces/app-state.interface';
import { ConfigService } from '@fcom/core/services/config/config.service';
import { ButtonMode, ButtonTheme, NotificationTheme } from '@fcom/ui-components';
import { LanguageService } from '@fcom/ui-translate/index';

import { ScrollService } from '../../../services/scroll/scroll.service';

@Component({
  selector: 'fin-login-card',
  templateUrl: './login-card.component.html',
  styleUrls: ['./login-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginCardComponent {
  private configService = inject(ConfigService);
  private gtmService = inject(GtmService);
  private joinService = inject(JoinService);
  private languageService = inject(LanguageService);
  private loginService = inject(LoginService);
  private scrollService = inject(ScrollService);
  private store$ = inject<Store<AppState>>(Store);

  customBenefits = input<string>();

  readonly localizedBenefits: Signal<string[]> = toSignal(
    toObservable(this.customBenefits).pipe(
      switchMap((customBenefits) => {
        return customBenefits
          ? this.languageService.translate<string[]>(customBenefits)
          : combineLatest(
              [
                'loginPromotion.benefitList.personalDetailsFilled',
                'loginPromotion.benefitList.earnPoints',
                'loginPromotion.benefitList.membershipBenefits',
              ].map((b) => this.languageService.translate<string>(b))
            );
      })
    ),
    { initialValue: [] as string[] }
  );
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonMode = ButtonMode;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;

  /**
   * Whether to show the Finnair Plus icon in the right upper corner
   */
  @Input()
  showIcon: boolean;

  @Input()
  footnoteText: string;

  /**
   * Mode to determine how to show benefits of logging in.
   * `list` shows a list and `text` shows a sentence.
   */
  @Input()
  benefitMode: 'list' | 'text' = 'list';

  @Input()
  benefitText: string;

  /**
   * Title shown at the top of the card
   */
  @Input()
  title: string;

  @Input()
  showForgotPassword = false;

  @Input()
  scrollOnCta = false;

  onJoinClick(): void {
    this.gtmService.trackElement(
      'join-promotion',
      GaContext.FINNAIR_PLUS,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
    if (this.configService.cfg.enableJoinDialog) {
      if (this.scrollOnCta) {
        this.scrollService.scrollTop();
      }
      this.joinService.joinDialogOpen$.next(true);
    } else {
      this.loginService.redirectToJoin();
    }
  }

  onLoginClick(): void {
    this.gtmService.trackElement(
      'login-promotion',
      GaContext.FINNAIR_PLUS,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
    if (this.configService.cfg.enableLoginDialog) {
      if (this.scrollOnCta) {
        this.scrollService.scrollTop();
      }
      this.store$.dispatch(LoginActions.setLoginPhaseStep({ step: LoginStep.CREDENTIALS }));
    } else {
      this.loginService.redirectToLogin();
    }
  }

  onForgotPasswordClick(): void {
    this.gtmService.trackElement(
      'forgot-pwd-promotion',
      GaContext.FINNAIR_PLUS,
      ElementTypes.LINK,
      undefined,
      ElementActions.CLICK
    );
    this.loginService.redirectToForgotPassword();
  }
}
